import { redirect } from "@tanstack/react-router";
import {
  createPermissionEvaluator,
  useAuthStore,
  type PermissionExpression,
  type PermissionRequirement,
} from "@nasam/kernel";

function firstExpression(
  requirement: PermissionRequirement,
): PermissionExpression | undefined {
  if (Array.isArray(requirement)) {
    return (requirement as readonly PermissionExpression[])[0];
  }
  const object = requirement as {
    readonly anyOf?: readonly PermissionExpression[];
    readonly allOf?: readonly PermissionExpression[];
  };
  return object.allOf?.[0] ?? object.anyOf?.[0];
}

/**
 * A `beforeLoad` for a feature route that should be blocked unless the
 * session user satisfies `requirement`. Mirrors
 * `@nasam/core`'s `requirePermission` (packages/core/src/shared/routing) —
 * duplicated locally rather than imported, since business-module packages
 * don't depend on `@nasam/core`.
 */
export function requirePermission(requirement: PermissionRequirement) {
  return ({
    location,
  }: {
    readonly location: { readonly pathname: string; readonly searchStr?: string };
  }): void => {
    const state = useAuthStore.getState();
    const evaluator = createPermissionEvaluator(state.user?.permissions);
    if (evaluator.satisfies(requirement)) return;

    const from = `${location.pathname}${location.searchStr ?? ""}`;
    // eslint-disable-next-line @typescript-eslint/only-throw-error -- TanStack Router redirects by throwing.
    throw redirect({
      to: "/unauthorized",
      search: { permission: firstExpression(requirement) ?? "", from },
    });
  };
}
